import { FC, useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Modal } from '../../components/modal/modal'
import { CustomButton } from '../../components/customButton'
import { VoiceCard } from './voiceCard'
import classes from '../../styles/voices/standConfigModal.module.scss'
import ttsClasses from '../../styles/tts/ttsModal.module.scss'
import cn from 'classnames'

type TVoice = { id: string; name: string; gender?: 'male' | 'female'; lang?: string }

const MOCK_VOICES: TVoice[] = [
  { id: '1', name: 'Наташа', gender: 'female', lang: 'ru' },
  { id: '2', name: 'Лео', gender: 'male', lang: 'ru' },
  { id: '3', name: 'Артем', gender: 'male', lang: 'ru' },
  { id: '4', name: 'Вика', gender: 'female', lang: 'ru' },
  { id: '5', name: 'Elena', gender: 'female', lang: 'en' },
  { id: '6', name: 'Mark', gender: 'male', lang: 'en' },
  { id: '7', name: 'Борис', gender: 'male', lang: 'ru' },
  { id: '8', name: 'Алина', gender: 'female', lang: 'ru' },
]


const SortableVoiceCard: FC<{ voice: TVoice }> = ({ voice }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: voice.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={classes.cardWrap}>
      <VoiceCard name={voice.name} gender={voice.gender} lang={voice.lang} className={classes.card} />
    </div>
  )
}

interface StandConfigModalProps {
  onClose: () => void
}

export const StandConfigModal: FC<StandConfigModalProps> = ({ onClose }) => {
  const [voices, setVoices] = useState<TVoice[]>(MOCK_VOICES)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setVoices((prev) => {
      const oldIndex = prev.findIndex((v) => v.id === active.id)
      const newIndex = prev.findIndex((v) => v.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  return (
    <Modal
      isOpen
      title="Настройка стенда"
      subtitle="Можно настроить порядок голосов, который будет виден пользователям на стенде"
      handleClose={onClose}
      classes={{ modalContent: ttsClasses.ttsModal }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={voices.map((v) => v.id)} strategy={rectSortingStrategy}>
          <div className={cn(ttsClasses.ttsModalVoicesWrapper, classes.grid)}>
            {voices.map((voice) => (
              <SortableVoiceCard key={voice.id} voice={voice} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className={classes.footer}>
        <CustomButton className={classes.footerReset} onClick={() => setVoices(MOCK_VOICES)}>Сбросить</CustomButton>
        <CustomButton className={classes.footerSave} onClick={onClose}>Сохранить</CustomButton>
      </div>
    </Modal>
  )
}
